import { useHistory, useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { supportWidgetPaths } from "@circle-react/helpers/urlHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";
import { CIRCLE_KB_URL } from "../constants";
import { useSupportWidgetContextV2 } from "../context/supportWidgetContext";
import { useShowPost } from "../hooks/useShowPost";
import HeaderBackButton from "./common/HeaderBackButton";
import HeaderResizeButton from "./common/HeaderResizeButton";
import OpenInNewWindowLink from "./common/OpenInNewWindowLink";

interface HeaderBarProps {
  onClose: () => void;
  pageName?: string;
}

const HeaderBar = ({ pageName = "", onClose }: HeaderBarProps) => {
  const history = useHistory();
  const isSpaceGroupsRoutes = Boolean(
    useRouteMatch(supportWidgetPaths.spaceGroups()),
  );
  const isSpaceRoute = Boolean(useRouteMatch("/spaces/:spaceId/posts"));
  const postMatch = useRouteMatch<{ id: string }>("/posts/:id");
  const newsMatch = useRouteMatch<{ id: string }>("/news/:id");
  const isPostRoute = Boolean(postMatch);
  const isNewsRoute = Boolean(newsMatch);
  const isNewsListRoute = Boolean(useRouteMatch("/news"));
  const isEventsRoute = Boolean(useRouteMatch("/events"));
  const { currentSpaceProperties, isExpanded } = useSupportWidgetContextV2();
  const { data: post } = useShowPost(
    postMatch?.params?.id || newsMatch?.params?.id || "",
  );

  const urls = ["help-en.circle.so", "help-pt.circle.so", "pt-help.circle.so"];
  let postKbUrl = post?.post_url || CIRCLE_KB_URL;
  urls.forEach(url => {
    if (postKbUrl.includes(url)) {
      postKbUrl = postKbUrl.replace(url, "help.circle.so");
    }
  });

  const getHeaderTitle = () => {
    if (isSpaceGroupsRoutes) return t("support_widget.search_container.title");
    if ((isNewsRoute || isPostRoute) && post) return post.name || "";
    if (isNewsListRoute) return t("support_widget.news.news");
    if (isSpaceRoute) return currentSpaceProperties.name;
    if (isEventsRoute) return t("support_widget.events");
    return pageName;
  };

  const handleBack = () => {
    if (isSpaceRoute || isSpaceGroupsRoutes) {
      history.replace("/space_groups");
    } else {
      history.goBack();
    }
  };

  const isBackButtonVisible =
    isSpaceRoute ||
    isPostRoute ||
    isNewsRoute ||
    isNewsListRoute ||
    isEventsRoute;

  const isExpandButtonVisible = isPostRoute || isNewsRoute;

  const isSpecialHeaderVisible = isBackButtonVisible || isSpaceGroupsRoutes;

  if (isSpecialHeaderVisible) {
    return (
      <div
        className={classNames(
          "bg-primary sticky top-0 z-10 flex h-[52px] w-full shrink-0 items-center border-b px-2",
          "justify-between",
        )}
      >
        <div className="flex items-center">
          {isBackButtonVisible && <HeaderBackButton onClick={handleBack} />}
          <div
            className={classNames(
              "truncate px-2",
              isExpanded ? "max-w-[560px]" : "max-w-[250px]",
            )}
          >
            <Typography.LabelMd weight="semibold">
              {getHeaderTitle()}
            </Typography.LabelMd>
          </div>
        </div>
        <div className="ml-auto flex items-center space-x-1">
          {isExpandButtonVisible && <HeaderResizeButton />}
          {(isPostRoute || isNewsRoute) && (
            <OpenInNewWindowLink version="minimal" postUrl={postKbUrl} />
          )}
          <button
            type="button"
            className="hover:bg-tertiary flex h-8 w-8 items-center justify-center rounded-lg p-1"
            onClick={onClose}
          >
            <Icon type="16-cross" size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Default header with page name and close button
  return (
    <div className="bg-primary sticky top-0 z-10 flex h-[52px] w-full shrink-0 items-center justify-between border-b px-4">
      <Typography.LabelMd weight="semibold">
        {getHeaderTitle()}
      </Typography.LabelMd>
      <button
        type="button"
        className="flex h-8 w-8 items-center justify-center"
        onClick={onClose}
      >
        <Icon type="16-cross" size={16} />
      </button>
    </div>
  );
};

export default HeaderBar;
